import React, { useState, useEffect } from 'react';
import { authFetch } from '../lib/authFetch';
import { ArrowRightLeft, TrendingUp, Shield, DollarSign, CheckCircle, AlertTriangle, RefreshCw, Play, Zap, Calculator, Target } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const fmt = (v) => {
  if (Math.abs(v ?? 0) >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (Math.abs(v ?? 0) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v ?? 0) >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${(v ?? 0).toFixed(0)}`;
};

const PORTFOLIO_COLORS = {
  bedrock: 'bg-emerald-100 text-emerald-700',
  fortress: 'bg-sky-100 text-sky-700',
  horizon: 'bg-indigo-100 text-indigo-700',
  velocity: 'bg-amber-100 text-amber-700',
  summit: 'bg-orange-100 text-orange-700',
  nebula: 'bg-purple-100 text-purple-700',
};

const effColor = (eff) => {
  if (eff >= 0.90) return 'text-emerald-600';
  if (eff >= 0.75) return 'text-amber-600';
  return 'text-red-500';
};

const effBg = (eff) => {
  if (eff >= 0.90) return 'bg-emerald-500';
  if (eff >= 0.75) return 'bg-amber-500';
  return 'bg-red-500';
};

// Crossing Simulator Component
function CrossingSimulator({ portfolios, onClose, isOpen }) {
  const [scenario, setScenario] = useState({
    dailyRedemption: 500000, // $500K daily redemption
    depositRate: 0.3, // 30% of redemption comes back as deposits (crossing)
    selectedPortfolio: 'all',
  });
  const [simResult, setSimResult] = useState(null);
  const [isSimulating, setIsSimulating] = useState(false);

  // AUM of the currently selected scope — drives % of AUM maths
  const scopedPortfolios = scenario.selectedPortfolio === 'all'
    ? portfolios
    : portfolios.filter(p => p.portfolioType === scenario.selectedPortfolio);
  const scopedAUM = scopedPortfolios.reduce((s, x) => s + (x.totalAUM || 0), 0);
  const redemptionPctOfAUM = scopedAUM > 0 ? (scenario.dailyRedemption / scopedAUM) * 100 : 0;

  const runSimulation = () => {
    setIsSimulating(true);
    
    // Simulate with a small delay for effect
    setTimeout(() => {
      const { dailyRedemption, depositRate, selectedPortfolio } = scenario;
      
      // Calculate totals
      let totalLiquidBuffer = 0;
      let totalAUM = 0;
      const portfolioBreakdown = [];
      
      const targetPortfolios = selectedPortfolio === 'all' 
        ? portfolios 
        : portfolios.filter(p => p.portfolioType === selectedPortfolio);
      const scopeAUMTotal = targetPortfolios.reduce((s, x) => s + (x.totalAUM || 0), 0);
      
      targetPortfolios.forEach(p => {
        const liquidBuffer = p.metrics?.liquidBuffer || 0;
        const aum = p.totalAUM || 0;
        totalLiquidBuffer += liquidBuffer;
        totalAUM += aum;
        
        // Each portfolio's share of the redemption — proportional to AUM
        // WITHIN THE SELECTED SCOPE (single-portfolio scope gets 100%)
        const aumShare = aum / (scopeAUMTotal || 1);
        const portfolioRedemption = dailyRedemption * aumShare;
        const netDailyDrain = portfolioRedemption * (1 - depositRate);
        const daysSupported = netDailyDrain > 0 ? Math.floor(liquidBuffer / netDailyDrain) : 999;
        
        portfolioBreakdown.push({
          name: p.portfolioType,
          liquidBuffer,
          aum,
          dailyDrain: netDailyDrain,
          dailyDrainPct: aum > 0 ? (netDailyDrain / aum) * 100 : 0,
          daysSupported: Math.min(daysSupported, 999),
          tier1: p.metrics?.tier1Amount || 0,
          tier2: p.metrics?.tier2Amount || 0,
          tier3: p.metrics?.tier3Amount || 0,
        });
      });
      
      // System-wide calculation
      const netDailyDrain = dailyRedemption * (1 - depositRate);
      const totalDaysSupported = netDailyDrain > 0 ? Math.floor(totalLiquidBuffer / netDailyDrain) : 999;
      
      // Tier depletion timeline — tiers are consumed strictly in sequence:
      // Tier 1 (T+0/T+1 cash & T-bills) → Tier 2 (T+2 listed) → Tier 3 (T+2–T+5 short credit)
      const tier1Total = portfolioBreakdown.reduce((s, p) => s + p.tier1, 0);
      const tier2Total = portfolioBreakdown.reduce((s, p) => s + p.tier2, 0);
      const tier3Total = portfolioBreakdown.reduce((s, p) => s + p.tier3, 0);
      
      const tier1Days = netDailyDrain > 0 ? Math.floor(tier1Total / netDailyDrain) : 999;
      const tier2Days = netDailyDrain > 0 ? Math.floor((tier1Total + tier2Total) / netDailyDrain) : 999;
      const tier3Days = totalDaysSupported;
      
      // Risk assessment
      let riskLevel = 'LOW';
      let riskColor = 'emerald';
      let riskMessage = 'System can handle this scenario comfortably';
      
      if (totalDaysSupported < 30) {
        riskLevel = 'CRITICAL';
        riskColor = 'red';
        riskMessage = 'Wholesale fund redemptions required within 30 days';
      } else if (totalDaysSupported < 90) {
        riskLevel = 'ELEVATED';
        riskColor = 'amber';
        riskMessage = 'Monitor closely - rebalancing recommended';
      } else if (totalDaysSupported < 180) {
        riskLevel = 'MODERATE';
        riskColor = 'yellow';
        riskMessage = 'Sustainable with normal market conditions';
      }
      
      setSimResult({
        totalLiquidBuffer,
        totalAUM,
        dailyRedemption,
        redemptionPct: totalAUM > 0 ? (dailyRedemption / totalAUM) * 100 : 0,
        netDrainPct: totalAUM > 0 ? (netDailyDrain / totalAUM) * 100 : 0,
        depositRate,
        netDailyDrain,
        totalDaysSupported: Math.min(totalDaysSupported, 999),
        crossingEfficiency: depositRate,
        portfolioBreakdown,
        tierDepletion: {
          tier1Days: Math.min(tier1Days, 999),
          tier2Days: Math.min(tier2Days, 999),
          tier3Days: Math.min(tier3Days, 999),
        },
        risk: { level: riskLevel, color: riskColor, message: riskMessage },
        tier1Total, tier2Total, tier3Total,
      });
      
      setIsSimulating(false);
    }, 800);
  };

  // Presets are expressed as % of scoped AUM so they stay realistic at any
  // fund size. Historical reference: normal daily redemptions run 0.2-0.3%
  // of AUM; the worst month of GFC-style stress ran ~2%/day; a true run ~5%.
  const presets = [
    { name: 'Normal Day', pct: 0.0025, deposit: 0.85, desc: '0.25% AUM/day · typical activity' },
    { name: 'Market Volatility', pct: 0.0075, deposit: 0.45, desc: '0.75% AUM/day · elevated redemptions' },
    { name: 'Black Swan Event', pct: 0.02, deposit: 0.15, desc: '2% AUM/day · GFC-level stress' },
    { name: 'Bank Run Scenario', pct: 0.05, deposit: 0.05, desc: '5% AUM/day · worst case' },
  ].map(p => ({ ...p, redemption: Math.round((p.pct * scopedAUM) / 1000) * 1000 }));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Zap className="w-5 h-5 text-indigo-600" />
            Liquidity Stress Test Simulator
          </DialogTitle>
          <DialogDescription>
            Model redemption scenarios to see how long the liquid buffer can sustain outflows without touching illiquid assets
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-4">
          {/* Preset Scenarios */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Quick Scenarios</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {presets.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => setScenario({ ...scenario, dailyRedemption: preset.redemption, depositRate: preset.deposit })}
                  data-testid={`sim-preset-${preset.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`p-3 rounded-lg border text-left transition-all hover:border-indigo-300 hover:bg-indigo-50 ${
                    scenario.dailyRedemption === preset.redemption && scenario.depositRate === preset.deposit
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200'
                  }`}
                >
                  <p className="font-medium text-sm text-gray-900">{preset.name}</p>
                  <p className="text-xs text-gray-500">{preset.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Daily Redemption Amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={scenario.dailyRedemption}
                  onChange={(e) => setScenario({ ...scenario, dailyRedemption: Number(e.target.value) })}
                  className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="500000"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1" data-testid="sim-redemption-pct">
                {fmt(scenario.dailyRedemption)} per day · <span className={`font-semibold ${redemptionPctOfAUM > 2 ? 'text-red-600' : redemptionPctOfAUM > 0.75 ? 'text-amber-600' : 'text-emerald-600'}`}>{redemptionPctOfAUM.toFixed(2)}% of AUM</span>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Crossing Rate (Deposits offsetting redemptions)
              </label>
              <div className="relative">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={scenario.depositRate * 100}
                  onChange={(e) => setScenario({ ...scenario, depositRate: Number(e.target.value) / 100 })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {(scenario.depositRate * 100).toFixed(0)}% of redemptions offset by new deposits
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Portfolio Scope
              </label>
              <select
                value={scenario.selectedPortfolio}
                onChange={(e) => setScenario({ ...scenario, selectedPortfolio: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">All Portfolios (System-wide)</option>
                {portfolios.map((p) => (
                  <option key={p.portfolioType} value={p.portfolioType}>
                    {p.name || p.portfolioType}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Run Button */}
          <div className="flex justify-center">
            <Button
              onClick={runSimulation}
              disabled={isSimulating}
              className="bg-indigo-600 hover:bg-indigo-700 px-8 py-3 text-lg"
            >
              {isSimulating ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  Simulating...
                </>
              ) : (
                <>
                  <Play className="w-5 h-5 mr-2" />
                  Run Stress Test
                </>
              )}
            </Button>
          </div>

          {/* Results */}
          {simResult && (
            <div className="space-y-4 pt-4 border-t">
              {/* Key Result */}
              <div className={`p-6 rounded-xl bg-gradient-to-r ${
                simResult.risk.color === 'emerald' ? 'from-emerald-50 to-emerald-100 border-emerald-200' :
                simResult.risk.color === 'amber' ? 'from-amber-50 to-amber-100 border-amber-200' :
                simResult.risk.color === 'yellow' ? 'from-yellow-50 to-yellow-100 border-yellow-200' :
                'from-red-50 to-red-100 border-red-200'
              } border`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                      Liquid Buffer Runway
                    </p>
                    <p className={`text-5xl font-bold mt-1 ${
                      simResult.risk.color === 'emerald' ? 'text-emerald-700' :
                      simResult.risk.color === 'amber' ? 'text-amber-700' :
                      simResult.risk.color === 'yellow' ? 'text-yellow-700' :
                      'text-red-700'
                    }`}>
                      {simResult.totalDaysSupported === 999 ? '999+' : simResult.totalDaysSupported} days
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      before illiquid asset sales required
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge className={`${
                      simResult.risk.color === 'emerald' ? 'bg-emerald-100 text-emerald-700' :
                      simResult.risk.color === 'amber' ? 'bg-amber-100 text-amber-700' :
                      simResult.risk.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    } text-sm px-3 py-1`}>
                      {simResult.risk.level} RISK
                    </Badge>
                    <p className="text-sm text-gray-600 mt-2 max-w-xs">
                      {simResult.risk.message}
                    </p>
                  </div>
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Total Liquid Buffer</p>
                  <p className="text-xl font-bold text-gray-900">{fmt(simResult.totalLiquidBuffer)}</p>
                  <p className="text-xs text-gray-400">{((simResult.totalLiquidBuffer / (simResult.totalAUM || 1)) * 100).toFixed(1)}% of AUM</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Daily Redemptions</p>
                  <p className="text-xl font-bold text-orange-600">{fmt(simResult.dailyRedemption)}</p>
                  <p className="text-xs text-gray-400">{simResult.redemptionPct.toFixed(2)}% of AUM / day</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Net Daily Drain</p>
                  <p className="text-xl font-bold text-red-600">{fmt(simResult.netDailyDrain)}</p>
                  <p className="text-xs text-gray-400">{simResult.netDrainPct.toFixed(2)}% of AUM / day</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Crossing Offset</p>
                  <p className="text-xl font-bold text-indigo-600">{(simResult.crossingEfficiency * 100).toFixed(0)}%</p>
                  <p className="text-xs text-gray-400">of redemptions matched</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Scoped AUM</p>
                  <p className="text-xl font-bold text-gray-900">{fmt(simResult.totalAUM)}</p>
                </div>
              </div>

              {/* Tier Depletion Timeline */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-indigo-600" />
                  Liquidity Tier Depletion Timeline
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-36 text-xs font-medium text-emerald-700">
                      Tier 1 · Cash + T-Bills
                      <span className="block text-[10px] text-gray-400 font-normal">Settles T+0 – T+1</span>
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-4 relative overflow-hidden">
                      <div 
                        className="absolute left-0 top-0 h-full bg-emerald-500 rounded-full transition-all"
                        style={{ width: `${Math.min(100, (simResult.tierDepletion.tier1Days / simResult.totalDaysSupported) * 100)}%` }}
                      />
                      <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white mix-blend-difference">
                        Days 1 – {simResult.tierDepletion.tier1Days === 999 ? '999+' : simResult.tierDepletion.tier1Days}
                      </span>
                    </div>
                    <div className="w-24 text-xs text-gray-500">{fmt(simResult.tier1Total)}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-36 text-xs font-medium text-sky-700">
                      Tier 2 · Listed / ETFs
                      <span className="block text-[10px] text-gray-400 font-normal">Settles T+2</span>
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-4 relative overflow-hidden">
                      <div 
                        className="absolute left-0 top-0 h-full bg-sky-500 rounded-full transition-all"
                        style={{ width: `${Math.min(100, (simResult.tierDepletion.tier2Days / simResult.totalDaysSupported) * 100)}%` }}
                      />
                      <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white mix-blend-difference">
                        Days {simResult.tierDepletion.tier1Days === 999 ? '—' : simResult.tierDepletion.tier1Days + 1} – {simResult.tierDepletion.tier2Days === 999 ? '999+' : simResult.tierDepletion.tier2Days}
                      </span>
                    </div>
                    <div className="w-24 text-xs text-gray-500">{fmt(simResult.tier2Total)}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-36 text-xs font-medium text-indigo-700">
                      Tier 3 · Short Credit
                      <span className="block text-[10px] text-gray-400 font-normal">Settles T+2 – T+5</span>
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-4 relative overflow-hidden">
                      <div 
                        className="absolute left-0 top-0 h-full bg-indigo-500 rounded-full transition-all"
                        style={{ width: '100%' }}
                      />
                      <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white mix-blend-difference">
                        Days {simResult.tierDepletion.tier2Days === 999 ? '—' : simResult.tierDepletion.tier2Days + 1} – {simResult.tierDepletion.tier3Days === 999 ? '999+' : simResult.tierDepletion.tier3Days}
                      </span>
                    </div>
                    <div className="w-24 text-xs text-gray-500">{fmt(simResult.tier3Total)}</div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Tiers deplete in strict sequence — cheapest liquidity first. A $1M Tier 1 operational cash floor is maintained system-wide at all times. Beyond Day {simResult.tierDepletion.tier3Days === 999 ? '999+' : simResult.tierDepletion.tier3Days}, wholesale fund redemptions (monthly window) would be required.
                </p>
              </div>

              {/* Portfolio Breakdown */}
              {simResult.portfolioBreakdown.length > 1 && (
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Portfolio</TableHead>
                        <TableHead className="text-right">Liquid Buffer</TableHead>
                        <TableHead className="text-right">Daily Drain</TableHead>
                        <TableHead className="text-right">Drain % AUM</TableHead>
                        <TableHead className="text-right">Days Supported</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {simResult.portfolioBreakdown.map((p) => (
                        <TableRow key={p.name}>
                          <TableCell>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PORTFOLIO_COLORS[p.name] || 'bg-gray-100 text-gray-700'}`}>
                              {p.name}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-medium">{fmt(p.liquidBuffer)}</TableCell>
                          <TableCell className="text-right text-red-600">{fmt(p.dailyDrain)}</TableCell>
                          <TableCell className="text-right text-gray-500 text-sm">{p.dailyDrainPct.toFixed(3)}%</TableCell>
                          <TableCell className="text-right">
                            <span className={`font-bold ${p.daysSupported > 90 ? 'text-emerald-600' : p.daysSupported > 30 ? 'text-amber-600' : 'text-red-600'}`}>
                              {p.daysSupported === 999 ? '999+' : p.daysSupported}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Investor Takeaway */}
              <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                <p className="font-medium text-indigo-900 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Key Insight for Investors
                </p>
                <p className="text-sm text-indigo-700 mt-1">
                  {simResult.totalDaysSupported >= 180 
                    ? `Even under this ${scenario.dailyRedemption >= 5000000 ? 'extreme' : 'elevated'} stress scenario, Trove's liquid buffer can sustain redemptions for ${simResult.totalDaysSupported} days without touching illiquid property or credit assets. The order crossing system automatically offsets ${(simResult.crossingEfficiency * 100).toFixed(0)}% of redemptions with incoming deposits, dramatically reducing net outflows.`
                    : simResult.totalDaysSupported >= 30
                    ? `This scenario would require careful management. The liquid buffer provides ${simResult.totalDaysSupported} days of coverage before wholesale fund redemptions become necessary. Active rebalancing strategies would be employed.`
                    : `This extreme scenario would trigger wholesale fund redemptions within ${simResult.totalDaysSupported} days. This represents a black swan event that the system monitors continuously with early warning alerts.`
                  }
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function OrderCrossing() {
  const [flows, setFlows] = useState([]);
  const [aggFlows, setAggFlows] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [showSimulator, setShowSimulator] = useState(false);
  
  // Live Crossing Test state
  const [liveTest, setLiveTest] = useState({
    portfolio: 'bedrock',
    redemptions: 100000,
    deposits: 60000,
  });
  const [liveResult, setLiveResult] = useState(null);
  const [runningLive, setRunningLive] = useState(false);
  const [alerts, setAlerts] = useState([]);

  const load = async (isInitial = true) => {
    if (isInitial) setLoading(true);
    try {
      const [flowsRes, aggRes, portfoliosRes, alertsRes] = await Promise.all([
        authFetch(`${API_BASE}/api/lms/daily-flows?limit=180`),
        authFetch(`${API_BASE}/api/lms/aggregate-flows`),
        authFetch(`${API_BASE}/api/lms/portfolios`),
        authFetch(`${API_BASE}/api/lms/waterfall/alerts`),
      ]);
      if (flowsRes.ok) setFlows(await flowsRes.json());
      if (aggRes.ok) setAggFlows(await aggRes.json());
      if (portfoliosRes.ok) setPortfolios(await portfoliosRes.json());
      if (alertsRes.ok) setAlerts(await alertsRes.json());
    } catch (e) { console.error(e); }
    if (isInitial) setLoading(false);
  };

  useEffect(() => { load(true); }, []);

  const runCrossing = async () => {
    setRunning(true);
    try {
      const res = await authFetch(`${API_BASE}/api/lms/run-daily-crossing`, { method: 'POST' });
      if (res.ok) { await load(false); }
    } catch {}
    setRunning(false);
  };
  
  const runLiveCrossing = async () => {
    setRunningLive(true);
    setLiveResult(null);
    try {
      const res = await authFetch(`${API_BASE}/api/lms/waterfall/process-crossing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          portfolio_type: liveTest.portfolio,
          gross_redemptions: liveTest.redemptions,
          gross_deposits: liveTest.deposits,
        }),
      });
      if (res.ok) {
        const result = await res.json();
        setLiveResult(result);
        await load(false); // Refresh data without resetting tabs
      }
    } catch (e) { console.error(e); }
    setRunningLive(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
    </div>
  );

  // Summary calculations from aggregate flows
  const sortedAgg = [...aggFlows].sort((a, b) => new Date(b.date) - new Date(a.date));
  const latestAgg = sortedAgg[0] || {};
  const totalRed = aggFlows.reduce((s, f) => s + (f.totals?.grossRedemptions ?? 0), 0);
  const totalDep = aggFlows.reduce((s, f) => s + (f.totals?.grossDeposits ?? 0), 0);
  const matched = aggFlows.reduce((s, f) => s + Math.min(f.totals?.grossRedemptions ?? 0, f.totals?.grossDeposits ?? 0), 0);
  const avgEff = aggFlows.length ? aggFlows.reduce((s, f) => s + (f.systemCrossingEfficiency ?? 0), 0) / aggFlows.length : 0;
  const costsSaved = matched * 0.02; // 2% transaction cost saved
  const wholesaleNeededDays = aggFlows.filter(f =>
    f.wholesaleFundRedemptionsNeeded?.fund1 || f.wholesaleFundRedemptionsNeeded?.fund2
  ).length;

  // Unique dates for flows table
  const dateGroups = flows.reduce((acc, f) => {
    const d = new Date(f.date).toLocaleDateString('en-AU');
    if (!acc[d]) acc[d] = [];
    acc[d].push(f);
    return acc;
  }, {});

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Order Crossing Engine</h1>
          <p className="text-sm text-gray-500 mt-1">
            Daily netting of investor deposits vs redemptions to avoid illiquid asset sales
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={() => setShowSimulator(true)} 
            variant="outline" 
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            data-testid="crossing-simulator-btn"
          >
            <Zap className="w-4 h-4 mr-2" />
            Crossing Simulator
          </Button>
          <Button onClick={runCrossing} disabled={running} className="bg-indigo-600 hover:bg-indigo-700">
            {running ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Running...</>
            ) : (
              <><ArrowRightLeft className="w-4 h-4 mr-2" /> Run Today's Crossing</>
            )}
          </Button>
        </div>
      </div>

      {/* Key metrics — the investor story */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              <p className="text-xs text-emerald-700 uppercase tracking-wide font-medium">Illiquid Fund Sales</p>
            </div>
            <p className="text-3xl font-bold text-emerald-700">$0</p>
            <p className="text-xs text-emerald-600 mt-1">ZERO days required — 30 days</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Avg Crossing Efficiency</p>
            <p className={`text-3xl font-bold ${effColor(avgEff)}`}>{(avgEff * 100).toFixed(1)}%</p>
            <p className="text-xs text-gray-400 mt-1">Deposits matched to redemptions</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Transaction Costs Saved</p>
            <p className="text-3xl font-bold text-indigo-600">{fmt(costsSaved)}</p>
            <p className="text-xs text-gray-400 mt-1">2% cost avoided per matched $</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Redemptions Handled</p>
            <p className="text-3xl font-bold text-gray-900">{fmt(totalRed)}</p>
            <p className="text-xs text-emerald-600 mt-1">All serviced from liquid buffer</p>
          </CardContent>
        </Card>
      </div>

      {/* Today's crossing */}
      {latestAgg.totals && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4 text-indigo-600" />
              Latest Crossing Result — {new Date(latestAgg.date).toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-3 bg-orange-50 rounded-lg">
                <p className="text-xs text-orange-600 font-medium">Gross Redemptions</p>
                <p className="text-xl font-bold text-orange-700">{fmt(latestAgg.totals.grossRedemptions)}</p>
              </div>
              <div className="text-center p-3 bg-emerald-50 rounded-lg">
                <p className="text-xs text-emerald-600 font-medium">Gross Deposits</p>
                <p className="text-xl font-bold text-emerald-700">{fmt(latestAgg.totals.grossDeposits)}</p>
              </div>
              <div className="text-center p-3 bg-sky-50 rounded-lg">
                <p className="text-xs text-sky-600 font-medium">Net Flow</p>
                <p className={`text-xl font-bold ${(latestAgg.totals.netFlow ?? 0) >= 0 ? 'text-sky-700' : 'text-red-600'}`}>
                  {(latestAgg.totals.netFlow ?? 0) >= 0 ? '+' : ''}{fmt(latestAgg.totals.netFlow)}
                </p>
              </div>
              <div className="text-center p-3 bg-indigo-50 rounded-lg">
                <p className="text-xs text-indigo-600 font-medium">Crossing Efficiency</p>
                <p className={`text-xl font-bold ${effColor(latestAgg.systemCrossingEfficiency)}`}>
                  {((latestAgg.systemCrossingEfficiency ?? 0) * 100).toFixed(1)}%
                </p>
              </div>
              <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-xs text-emerald-600 font-medium">Illiquid Assets Sold</p>
                <p className="text-xl font-bold text-emerald-700">$0</p>
                <p className="text-xs text-emerald-500">Property + Credit protected</p>
              </div>
            </div>

            {/* Per portfolio */}
            {latestAgg.portfolios && (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                {Object.entries(latestAgg.portfolios).map(([pt, pf]) => (
                  <div key={pt} className="border rounded-lg p-2">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${PORTFOLIO_COLORS[pt] || 'bg-gray-100 text-gray-700'}`}>
                      {pt}
                    </span>
                    <div className="mt-1.5 space-y-0.5 text-xs text-gray-600">
                      <div className="flex justify-between">
                        <span>Dep</span>
                        <span className="text-emerald-600 font-medium">{fmt(pf.deposits)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Red</span>
                        <span className="text-orange-600 font-medium">{fmt(pf.redemptions)}</span>
                      </div>
                      <div className="flex justify-between border-t pt-0.5">
                        <span>Net</span>
                        <span className={`font-semibold ${(pf.netFlow ?? 0) >= 0 ? 'text-sky-600' : 'text-red-500'}`}>
                          {(pf.netFlow ?? 0) >= 0 ? '+' : ''}{fmt(pf.netFlow)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="history">
        <TabsList>
          <TabsTrigger value="history">30-Day Crossing History</TabsTrigger>
          <TabsTrigger value="flows">Daily Flow Detail</TabsTrigger>
          <TabsTrigger value="livetest">Live Crossing Test</TabsTrigger>
          <TabsTrigger value="alerts">Alerts ({alerts.length})</TabsTrigger>
        </TabsList>
        
        {/* Live Crossing Test Tab */}
        <TabsContent value="livetest">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Panel */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Test Real Waterfall Logic</CardTitle>
                <CardDescription>
                  This will ACTUALLY deplete the liquid buffer in tiered order (Tier 1 → 2 → 3 → Wholesale)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Portfolio</label>
                  <select
                    value={liveTest.portfolio}
                    onChange={(e) => setLiveTest({ ...liveTest, portfolio: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    {portfolios.map(p => (
                      <option key={p.portfolioType} value={p.portfolioType}>{p.name || p.portfolioType}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Redemptions</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        value={liveTest.redemptions}
                        onChange={(e) => setLiveTest({ ...liveTest, redemptions: Number(e.target.value) })}
                        className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deposits</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        value={liveTest.deposits}
                        onChange={(e) => setLiveTest({ ...liveTest, deposits: Number(e.target.value) })}
                        className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    <strong>Warning:</strong> This is a LIVE test. It will actually modify the portfolio's buffer balances.
                  </p>
                </div>
                <Button onClick={runLiveCrossing} disabled={runningLive} className="w-full bg-indigo-600 hover:bg-indigo-700">
                  {runningLive ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                  ) : (
                    <><Play className="w-4 h-4 mr-2" /> Run Live Crossing</>
                  )}
                </Button>
              </CardContent>
            </Card>
            
            {/* Results Panel */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Waterfall Result</CardTitle>
                <CardDescription>How the redemption shortfall was funded</CardDescription>
              </CardHeader>
              <CardContent>
                {!liveResult ? (
                  <div className="text-center py-8 text-gray-500">
                    Run a crossing test to see results
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Summary */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-orange-50 rounded-lg">
                        <p className="text-xs text-orange-600">Redemptions</p>
                        <p className="text-lg font-bold text-orange-700">{fmt(liveResult.total_redemptions)}</p>
                      </div>
                      <div className="p-3 bg-emerald-50 rounded-lg">
                        <p className="text-xs text-emerald-600">Deposits</p>
                        <p className="text-lg font-bold text-emerald-700">{fmt(liveResult.total_deposits)}</p>
                      </div>
                    </div>
                    
                    {/* Shortfall */}
                    <div className={`p-4 rounded-lg ${liveResult.net_shortfall > 0 ? 'bg-red-50 border border-red-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                      <p className="text-sm font-medium">Net Shortfall (Buffer Needed)</p>
                      <p className={`text-2xl font-bold ${liveResult.net_shortfall > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {fmt(liveResult.net_shortfall)}
                      </p>
                    </div>
                    
                    {/* Funding Sources */}
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Funding Sources:</p>
                      {liveResult.funded_by_crossing > 0 && (
                        <div className="flex justify-between items-center p-2 bg-sky-50 rounded">
                          <span className="text-sm flex items-center gap-2">
                            <ArrowRightLeft className="w-4 h-4 text-sky-600" />
                            Order Crossing
                          </span>
                          <span className="font-medium text-sky-700">{fmt(liveResult.funded_by_crossing)}</span>
                        </div>
                      )}
                      {liveResult.funded_by_tier1 > 0 && (
                        <div className="flex justify-between items-center p-2 bg-emerald-50 rounded">
                          <span className="text-sm">Tier 1 (Cash + T-Bills)</span>
                          <span className="font-medium text-emerald-700">{fmt(liveResult.funded_by_tier1)}</span>
                        </div>
                      )}
                      {liveResult.funded_by_tier2 > 0 && (
                        <div className="flex justify-between items-center p-2 bg-amber-50 rounded">
                          <span className="text-sm">Tier 2 (ETFs + Futures)</span>
                          <span className="font-medium text-amber-700">{fmt(liveResult.funded_by_tier2)}</span>
                        </div>
                      )}
                      {liveResult.funded_by_tier3 > 0 && (
                        <div className="flex justify-between items-center p-2 bg-orange-50 rounded">
                          <span className="text-sm">Tier 3 (Bonds)</span>
                          <span className="font-medium text-orange-700">{fmt(liveResult.funded_by_tier3)}</span>
                        </div>
                      )}
                      {liveResult.funded_by_wholesale > 0 && (
                        <div className="flex justify-between items-center p-2 bg-red-100 rounded border border-red-300">
                          <span className="text-sm font-medium text-red-700 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            WHOLESALE FUND REDEMPTION
                          </span>
                          <span className="font-bold text-red-700">{fmt(liveResult.funded_by_wholesale)}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* New Buffer State */}
                    <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-700 mb-2">New Buffer Balances:</p>
                      <div className="grid grid-cols-4 gap-2 text-center text-xs">
                        <div className="p-2 bg-white rounded">
                          <p className="text-gray-500">Tier 1</p>
                          <p className="font-semibold">{fmt(liveResult.new_tier1_balance)}</p>
                        </div>
                        <div className="p-2 bg-white rounded">
                          <p className="text-gray-500">Tier 2</p>
                          <p className="font-semibold">{fmt(liveResult.new_tier2_balance)}</p>
                        </div>
                        <div className="p-2 bg-white rounded">
                          <p className="text-gray-500">Tier 3</p>
                          <p className="font-semibold">{fmt(liveResult.new_tier3_balance)}</p>
                        </div>
                        <div className="p-2 bg-indigo-50 rounded">
                          <p className="text-indigo-600">Total</p>
                          <p className="font-bold text-indigo-700">{fmt(liveResult.new_total_buffer)}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Alerts */}
                    {(liveResult.wholesale_redemption_required || liveResult.buffer_critically_low) && (
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-sm font-medium text-red-700 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Alerts Generated
                        </p>
                        {liveResult.wholesale_redemption_required && (
                          <p className="text-xs text-red-600 mt-1">• CRITICAL: Wholesale fund redemption required</p>
                        )}
                        {liveResult.buffer_critically_low && (
                          <p className="text-xs text-red-600 mt-1">• WARNING: Buffer critically low (&lt;10% target)</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Alerts Tab */}
        <TabsContent value="alerts">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                Open Liquidity Alerts
              </CardTitle>
              <CardDescription>Alerts that require attention</CardDescription>
            </CardHeader>
            <CardContent>
              {alerts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                  No open alerts. All systems operating normally.
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.map((alert, i) => (
                    <div 
                      key={i} 
                      className={`p-4 rounded-lg border ${
                        alert.severity === 'CRITICAL' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <Badge className={alert.severity === 'CRITICAL' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                            {alert.severity}
                          </Badge>
                          <p className="font-medium mt-1">{alert.type.replace(/_/g, ' ')}</p>
                          <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PORTFOLIO_COLORS[alert.portfolio_type] || 'bg-gray-100 text-gray-700'}`}>
                          {alert.portfolio_type}
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        Created: {new Date(alert.created_at).toLocaleString('en-AU')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aggregate history */}
        <TabsContent value="history">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Redemptions</TableHead>
                    <TableHead className="text-right">Deposits</TableHead>
                    <TableHead className="text-right">Net Flow</TableHead>
                    <TableHead>Efficiency</TableHead>
                    <TableHead>Illiquid Sales</TableHead>
                    <TableHead>Helix Outcome</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedAgg.map((f, i) => {
                    const eff = f.systemCrossingEfficiency ?? 0;
                    const wfNeeded = f.wholesaleFundRedemptionsNeeded?.fund1 || f.wholesaleFundRedemptionsNeeded?.fund2;
                    return (
                      <TableRow key={i}>
                        <TableCell className="text-sm font-medium">
                          {new Date(f.date).toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </TableCell>
                        <TableCell className="text-right text-orange-600">{fmt(f.totals?.grossRedemptions)}</TableCell>
                        <TableCell className="text-right text-emerald-600">{fmt(f.totals?.grossDeposits)}</TableCell>
                        <TableCell className={`text-right font-medium ${(f.totals?.netFlow ?? 0) >= 0 ? 'text-sky-600' : 'text-red-500'}`}>
                          {(f.totals?.netFlow ?? 0) >= 0 ? '+' : ''}{fmt(f.totals?.netFlow)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={eff * 100} className="w-16 h-2" indicatorClassName={effBg(eff)} />
                            <span className={`text-sm font-medium ${effColor(eff)}`}>{(eff * 100).toFixed(1)}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {wfNeeded ? (
                            <Badge className="bg-red-100 text-red-700 text-xs">Required</Badge>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">None</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {eff >= 0.90 ? (
                            <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Excellent</span>
                          ) : eff >= 0.75 ? (
                            <span className="text-xs text-amber-600">Good</span>
                          ) : (
                            <span className="text-xs text-red-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Monitor</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Daily Flow Detail */}
        <TabsContent value="flows">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Portfolio</TableHead>
                    <TableHead className="text-right">Redemptions</TableHead>
                    <TableHead className="text-right">Deposits</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                    <TableHead>Efficiency</TableHead>
                    <TableHead className="text-right">Costs Saved</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {flows.slice(0, 60).map((f, i) => {
                    const cross = f.crossing || {};
                    const eff = cross.efficiency ?? 0;
                    const netFlow = f.customerActivity?.netFlow ?? 0;
                    return (
                      <TableRow key={i}>
                        <TableCell className="text-xs text-gray-500">
                          {new Date(f.date).toLocaleDateString('en-AU')}
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PORTFOLIO_COLORS[f.portfolioId] || 'bg-gray-100 text-gray-700'}`}>
                            {f.portfolioId}
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-orange-600">{fmt(cross.grossRedemptions)}</TableCell>
                        <TableCell className="text-right text-emerald-600">{fmt(cross.grossDeposits)}</TableCell>
                        <TableCell className={`text-right font-medium ${netFlow >= 0 ? 'text-sky-600' : 'text-red-500'}`}>
                          {netFlow >= 0 ? '+' : ''}{fmt(netFlow)}
                        </TableCell>
                        <TableCell>
                          <span className={`text-sm font-medium ${effColor(eff)}`}>{(eff * 100).toFixed(1)}%</span>
                        </TableCell>
                        <TableCell className="text-right text-indigo-600 text-sm">
                          {fmt(cross.transactionCostsSaved ?? 0)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Crossing Simulator Modal */}
      <CrossingSimulator 
        portfolios={portfolios} 
        isOpen={showSimulator} 
        onClose={() => setShowSimulator(false)} 
      />
    </div>
  );
}
